import { useRef, useState, type ReactNode } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Info } from 'lucide-react';
import { businessReportsApi, type PnlIpdDirectCostBreakdownResponse } from '@/api/businessReports';
import { Popover, PopoverAnchor, PopoverContent } from '@/components/ui/popover';
import { pnlIpdDirectCostHint } from '@/lib/pnlIpdDirectLines';
import { cn } from '@/lib/utils';

type Props = {
  lineId: string;
  label: string;
  periodMode: 'MTD' | 'YTD';
  periodKey: string;
  fyStartYear?: number;
  hospitalId?: string;
  className?: string;
  children?: ReactNode;
};

function BreakdownPanel({
  data,
  loading,
  hintsOnly,
  label,
}: {
  data?: PnlIpdDirectCostBreakdownResponse;
  loading: boolean;
  hintsOnly: boolean;
  label: string;
}) {
  const hint = pnlIpdDirectCostHint(data?.lineId ?? '');

  if (hintsOnly) {
    return (
      <p className="text-[11px] text-muted-foreground leading-snug">
        {hint?.note ?? 'Hover a month value for discharged IPD detail.'}
      </p>
    );
  }

  if (loading) {
    return <p className="text-xs text-muted-foreground">Loading IPD breakdown…</p>;
  }

  if (!data) {
    return <p className="text-xs text-muted-foreground">No data.</p>;
  }

  return (
    <div className="space-y-2 max-w-lg">
      <p className="text-[11px] text-muted-foreground leading-snug">{data.sourceNote}</p>
      {data.showPartnerSummary && data.partnerSummary.length > 0 ? (
        <div>
          <p className="text-[11px] font-medium text-slate-700">By financing partner</p>
          <ul className="mt-1 text-[11px] space-y-0.5">
            {data.partnerSummary.map((p) => (
              <li key={p.partnerName} className="flex justify-between gap-3 tabular-nums">
                <span className="text-slate-600 truncate">{p.partnerName}</span>
                <span className="font-medium shrink-0">
                  ₹{p.amount.toLocaleString('en-IN')}
                  <span className="text-muted-foreground font-normal"> · {p.admissionCount} IPD</span>
                </span>
              </li>
            ))}
          </ul>
        </div>
      ) : null}
      {data.items.length === 0 ? (
        <p className="text-[11px] text-muted-foreground">No discharged IPDs with {label} in this period.</p>
      ) : (
        <div>
          <p className="text-[11px] font-medium text-slate-700">
            By patient / IPD ({data.periodLabel})
          </p>
          <ul className="mt-1 max-h-52 overflow-y-auto text-[11px] divide-y divide-slate-100">
            {data.items.map((item) => (
              <li key={item.admissionId} className="py-1.5 flex justify-between gap-2">
                <div className="min-w-0">
                  <p className="font-medium text-slate-800 truncate">{item.patientName}</p>
                  <p className="text-muted-foreground truncate">
                    {item.ipdNumber}
                    {item.uhid ? ` · ${item.uhid}` : ''}
                    {data.showPartnerSummary && item.financingPartnerName
                      ? ` · ${item.financingPartnerName}`
                      : ''}
                  </p>
                </div>
                <span className="tabular-nums font-medium shrink-0">
                  ₹{item.amount.toLocaleString('en-IN')}
                </span>
              </li>
            ))}
          </ul>
          {data.total > 0 ? (
            <p className="text-[11px] font-semibold text-right tabular-nums pt-1 border-t mt-1">
              Total ₹{data.total.toLocaleString('en-IN')}
            </p>
          ) : null}
        </div>
      )}
    </div>
  );
}

export function PnlIpdDirectCostBreakdownHover({
  lineId,
  label,
  periodMode,
  periodKey,
  fyStartYear,
  hospitalId,
  className,
  children,
}: Props) {
  const [open, setOpen] = useState(false);
  const closeTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const hintsOnly = !periodKey;

  const { data, isLoading } = useQuery({
    queryKey: [
      'pnl-ipd-direct-cost-breakdown',
      lineId,
      periodMode,
      periodKey,
      fyStartYear,
      hospitalId,
    ],
    queryFn: () =>
      businessReportsApi.pnlIpdDirectCostBreakdown({
        lineId,
        periodMode,
        month: periodMode === 'MTD' ? periodKey : undefined,
        fy: periodMode === 'YTD' ? fyStartYear : undefined,
        hospitalId,
      }),
    enabled: open && !hintsOnly,
    staleTime: 60_000,
  });

  const hint = pnlIpdDirectCostHint(lineId);
  if (!hint) {
    return <>{children ?? <span className={className}>{label}</span>}</>;
  }

  const clearCloseTimer = () => {
    if (closeTimerRef.current) {
      clearTimeout(closeTimerRef.current);
      closeTimerRef.current = null;
    }
  };

  const scheduleClose = () => {
    clearCloseTimer();
    closeTimerRef.current = setTimeout(() => setOpen(false), 120);
  };

  const handleEnter = () => {
    clearCloseTimer();
    setOpen(true);
  };

  return (
    <Popover open={open} modal={false}>
      <PopoverAnchor asChild>
        <span
          className={cn(
            'inline-flex items-center gap-1',
            children ? 'cursor-help w-full justify-end' : 'cursor-help',
            className
          )}
          onMouseEnter={handleEnter}
          onMouseLeave={scheduleClose}
          onFocus={handleEnter}
          onBlur={scheduleClose}
          tabIndex={children ? -1 : 0}
          role={children ? undefined : 'button'}
          aria-label={children ? undefined : `${label} — IPD cost breakdown`}
        >
          {children ?? (
            <>
              <span className="border-b border-dotted border-slate-400">{label}</span>
              <Info className="h-3.5 w-3.5 text-slate-400 shrink-0" />
            </>
          )}
        </span>
      </PopoverAnchor>
      <PopoverContent
        className="w-[min(24rem,calc(100vw-2rem))] p-3"
        align={children ? 'end' : 'start'}
        side="bottom"
        sideOffset={6}
        collisionPadding={12}
        onMouseEnter={handleEnter}
        onMouseLeave={scheduleClose}
        onOpenAutoFocus={(e) => e.preventDefault()}
        onCloseAutoFocus={(e) => e.preventDefault()}
      >
        <p className="text-xs font-semibold text-slate-900 mb-2">{label}</p>
        <BreakdownPanel
          data={data}
          loading={isLoading}
          hintsOnly={hintsOnly}
          label={label}
        />
      </PopoverContent>
    </Popover>
  );
}
